import Big from 'big.js';
import { Temporal } from 'temporal-polyfill';
import { z } from 'zod/v4';
import type { DateRangeString } from '~/utils/temporal';

export const zodBigNumber = z.preprocess(
  (val) => {
    if (typeof val === 'string') {
      try {
        return new Big(val);
      } catch {
        return undefined;
      }
    }

    if (val instanceof Big) {
      return val;
    }

    return undefined;
  },
  z.custom<Big>((val) => val instanceof Big, 'Cannot create Big number from input.'),
);

const datetimeOffset = z.iso.datetime({ offset: true });

export const zodIsoDate = z.preprocess((val) => {
  const isDateTime = datetimeOffset.safeParse(val);

  if (!isDateTime.success) {
    return undefined;
  }

  return Temporal.Instant.from(isDateTime.data).toString();
}, z.iso.datetime());

export const zodInstant = z.preprocess(
  (val) => {
    if (typeof val === 'string') {
      try {
        return Temporal.Instant.from(val);
      } catch {
        return undefined;
      }
    }

    if (val instanceof Temporal.Instant) {
      return val;
    }

    return undefined;
  },
  z.custom<Temporal.Instant>((val) => val instanceof Temporal.Instant, 'Cannot create Instant from input.'),
);

export const zodPlainDate = z.preprocess(
  (val) => {
    if (typeof val === 'string') {
      try {
        return Temporal.PlainDate.from(val);
      } catch {
        return undefined;
      }
    }

    if (val instanceof Temporal.PlainDate) {
      return val;
    }

    return undefined;
  },
  z.custom<Temporal.PlainDate>((val) => val instanceof Temporal.PlainDate, 'Cannot create PlainDate from input.'),
);

export const zodPlainYearMonth = z.preprocess(
  (val) => {
    if (typeof val === 'string') {
      try {
        return Temporal.PlainYearMonth.from(val);
      } catch {
        return undefined;
      }
    }

    if (val instanceof Temporal.PlainYearMonth) {
      return val;
    }

    return undefined;
  },
  z.custom<Temporal.PlainYearMonth>(
    (val) => val instanceof Temporal.PlainYearMonth,
    'Cannot create PlainYearMonth from input.',
  ),
);

// A `[start, end]` tuple of ISO strings (either bound nullable). Modelled as a tuple so the inferred
// type matches `DateRangeString` exactly.
export const zodDateRangeString: z.ZodType<DateRangeString> = z.tuple([z.string().nullable(), z.string().nullable()]);
